import { useEffect, useState } from 'react';
import { CONSENT_BASIS_LABELS, type ConsentBasis } from '@salesbee/contracts';
import { Alert, Badge, Button, Card, CardContent, Checkbox, Field, Input, PageHeader, Select, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, Textarea, formatDate, formatDateTime, useToast } from '@salesbee/ui';
import { QueryView, Section, useCurrent } from '../components/common';
import { errorMessage, trpc } from '../lib/trpc';

function windowLabel(w: { start: string; end: string } | null) {
  return w ? `${w.start}-${w.end}` : 'no calls';
}

export function CompliancePage() {
  const rules = trpc.compliance.rules.useQuery();
  const current = useCurrent();
  const canManage = current.data?.role === 'owner' || current.data?.role === 'admin';
  return (
    <div className="space-y-8">
      <PageHeader title="Compliance" description="Do-not-call list, calling hours, disclosure and caller ID. The legal floor is built in; you can only make it stricter." />
      <Alert variant="warning" title="Legal review required">
        These rules are a conservative reading of CRTC (Canada) and FTC/FCC (US) telemarketing rules. They are not legal advice: have telemarketing counsel review your scripts and settings before live calling.
      </Alert>
      <QueryView query={rules}>
        {(r) => (
          <>
            {!r.registryConfigured ? (
              <Alert variant="info" title="National DNC registry not configured">
                Calls to contacts without a recorded legal basis (consumer calls) are blocked. Record express consent, an existing business relationship or a B2B basis on contacts, or configure DNC_REGISTRY_URL.
              </Alert>
            ) : null}
            <CallingSettings disabled={!canManage} />
            <Section title="Rules in force" description={`Rule set ${r.version}. Calls must start ${r.endBufferMinutes} minutes before a window closes; at most ${r.maxAttemptsCeiling} attempts per contact.`}>
              <div className="grid gap-4 lg:grid-cols-2">
                {r.countries.map((c) => (
                  <Card key={c.code}>
                    <CardContent className="space-y-2 py-5 text-[14px]">
                      <h3 className="font-semibold">{c.name}</h3>
                      <p className="text-muted-foreground">{c.authority}</p>
                      <p>Weekdays {windowLabel(c.callingWindows.weekday)} · Saturday {windowLabel(c.callingWindows.saturday)} · Sunday {windowLabel(c.callingWindows.sunday)} (callee local time)</p>
                      <p>National registry: {c.registry}. Exempt: {c.registryExemptBases.map((b) => CONSENT_BASIS_LABELS[b as ConsentBasis]).join(', ')}.</p>
                      <p>AI voice allowed with: {c.aiVoiceAllowedBases.map((b) => CONSENT_BASIS_LABELS[b as ConsentBasis]).join(', ')}. {c.aiVoiceNote}</p>
                      <p>Internal DNC requests kept {Math.round(c.internalDncRetentionDays / 365)} years.</p>
                      {c.regionOverrides.length ? <p className="text-muted-foreground">Stricter: {c.regionOverrides.map((o) => `${o.region} (${o.note})`).join('; ')}</p> : null}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </Section>
          </>
        )}
      </QueryView>
      <DncList canRemove={canManage} />
      <Trail />
    </div>
  );
}

function CallingSettings({ disabled }: { disabled: boolean }) {
  const settings = trpc.workspace.settings.get.useQuery();
  const update = trpc.workspace.settings.update.useMutation();
  const utils = trpc.useUtils();
  const { success, error } = useToast();
  const [f, setF] = useState({ callerIdNumber: '', callerIdName: '', disclosureScript: '', recordCalls: true, wdStart: '', wdEnd: '', weStart: '', weEnd: '', blackout: '', maxAttempts: '3' });
  useEffect(() => {
    const o = settings.data?.outbound;
    if (!o) return;
    setF({
      callerIdNumber: o.callerIdNumber ?? '',
      callerIdName: o.callerIdName ?? '',
      disclosureScript: o.disclosureScript,
      recordCalls: o.recordCalls,
      wdStart: o.weekdayWindow?.start ?? '',
      wdEnd: o.weekdayWindow?.end ?? '',
      weStart: o.weekendWindow?.start ?? '',
      weEnd: o.weekendWindow?.end ?? '',
      blackout: o.blackoutDates.join(', '),
      maxAttempts: String(o.maxAttemptsPerContact),
    });
  }, [settings.data]);
  const preview = trpc.workspace.settings.previewDisclosure.useQuery({ script: f.disclosureScript }, { enabled: f.disclosureScript.length > 0 });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  const save = () =>
    update.mutate(
      {
        outbound: {
          callerIdNumber: f.callerIdNumber || null,
          callerIdName: f.callerIdName || null,
          disclosureScript: f.disclosureScript,
          recordCalls: f.recordCalls,
          weekdayWindow: f.wdStart && f.wdEnd ? { start: f.wdStart, end: f.wdEnd } : null,
          weekendWindow: f.weStart && f.weEnd ? { start: f.weStart, end: f.weEnd } : null,
          blackoutDates: f.blackout.split(',').map((s) => s.trim()).filter((s) => /^\d{4}-\d{2}-\d{2}$/.test(s)),
          maxAttemptsPerContact: Number(f.maxAttempts),
        },
        onboarding: { callingHours: true, disclosure: true },
      },
      { onSuccess: async () => { success('Compliance settings saved', 'The change is in the audit trail.'); await utils.invalidate(); }, onError: (e) => error('Could not save', errorMessage(e)) },
    );
  return (
    <Section title="Calling settings">
      <Card>
        <CardContent className="grid gap-4 py-6 sm:grid-cols-2">
          <Field label="Caller ID number"><Input disabled={disabled} value={f.callerIdNumber} onChange={set('callerIdNumber')} placeholder="+1 416 555 0100" /></Field>
          <Field label="Caller name"><Input disabled={disabled} value={f.callerIdName} onChange={set('callerIdName')} /></Field>
          <Field label="AI + recording disclosure (spoken first on every AI call)" className="sm:col-span-2" description="Use {business} for your company name.">
            <Textarea disabled={disabled} rows={3} value={f.disclosureScript} onChange={set('disclosureScript')} />
          </Field>
          {preview.data && !preview.data.ok ? <Alert variant="warning" className="sm:col-span-2">{preview.data.problems.join(' ')}</Alert> : null}
          <Checkbox disabled={disabled} label="Record calls" checked={f.recordCalls} onCheckedChange={(v) => setF((x) => ({ ...x, recordCalls: v === true }))} />
          <Field label="Max attempts per contact"><Select disabled={disabled} value={f.maxAttempts} onChange={set('maxAttempts')} options={['1', '2', '3', '4', '5', '6', '8', '10'].map((v) => ({ value: v, label: v }))} /></Field>
          <Field label="Stricter weekday window (optional)">
            <div className="flex gap-2"><Input disabled={disabled} type="time" value={f.wdStart} onChange={set('wdStart')} aria-label="Weekday start" /><Input disabled={disabled} type="time" value={f.wdEnd} onChange={set('wdEnd')} aria-label="Weekday end" /></div>
          </Field>
          <Field label="Stricter weekend window (optional)">
            <div className="flex gap-2"><Input disabled={disabled} type="time" value={f.weStart} onChange={set('weStart')} aria-label="Weekend start" /><Input disabled={disabled} type="time" value={f.weEnd} onChange={set('weEnd')} aria-label="Weekend end" /></div>
          </Field>
          <Field label="Blackout dates (YYYY-MM-DD, comma separated)" className="sm:col-span-2"><Input disabled={disabled} value={f.blackout} onChange={set('blackout')} placeholder="2026-12-25, 2027-01-01" /></Field>
          <div className="sm:col-span-2">
            <Button disabled={disabled} loading={update.isPending} onClick={save}>Save calling settings</Button>
            {disabled ? <span className="ml-3 text-sm text-muted-foreground">Only owners and admins can change these.</span> : null}
          </div>
        </CardContent>
      </Card>
    </Section>
  );
}

function DncList({ canRemove }: { canRemove: boolean }) {
  const [search, setSearch] = useState('');
  const list = trpc.compliance.dnc.list.useQuery({ search: search || undefined, limit: 200 });
  const add = trpc.compliance.dnc.add.useMutation();
  const remove = trpc.compliance.dnc.remove.useMutation();
  const utils = trpc.useUtils();
  const { success, error } = useToast();
  const [phone, setPhone] = useState('');
  const [reason, setReason] = useState('');
  return (
    <Section title="Do-not-call list" description="Checked before every call and text. Opt-outs on calls are added automatically.">
      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 py-5">
          <Field label="Phone"><Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(416) 555-0199" /></Field>
          <Field label="Reason" className="min-w-64 flex-1"><Input value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
          <Button disabled={!phone.trim()} loading={add.isPending} onClick={() => add.mutate({ phone, reason: reason || null, source: 'manual' }, { onSuccess: async () => { success('Added to the do-not-call list'); setPhone(''); setReason(''); await utils.compliance.dnc.list.invalidate(); }, onError: (e) => error('Could not add', errorMessage(e)) })}>
            Add number
          </Button>
          <Input className="ml-auto max-w-xs" placeholder="Search numbers" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search DNC" />
        </CardContent>
      </Card>
      <QueryView query={list}>
        {(data) => (
          <Card>
            <Table>
              <TableHeader><TableRow><TableHead>Number</TableHead><TableHead>Source</TableHead><TableHead>Reason</TableHead><TableHead>Added</TableHead><TableHead /></TableRow></TableHeader>
              <TableBody>
                {data.items.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell className="font-mono">{d.phone}</TableCell>
                    <TableCell><Badge variant={d.source === 'call_outcome' || d.source === 'opt_out' ? 'destructive' : 'default'}>{d.source.replace(/_/g, ' ')}</Badge></TableCell>
                    <TableCell>{d.reason ?? ''}</TableCell>
                    <TableCell>{formatDate(d.createdAt)}</TableCell>
                    <TableCell className="text-right">
                      {canRemove ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            const why = window.prompt('Why may this number be called again? (e.g. "Gave express consent by phone on 2026-10-01")');
                            if (why && why.trim().length >= 10) remove.mutate({ id: d.id, reason: why.trim() }, { onSuccess: () => void utils.compliance.dnc.list.invalidate() });
                          }}
                        >
                          Remove
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {data.items.length === 0 ? <p className="p-6 text-center text-sm text-muted-foreground">The list is empty.</p> : null}
          </Card>
        )}
      </QueryView>
    </Section>
  );
}

function Trail() {
  const trail = trpc.compliance.trail.useQuery({ limit: 50 });
  return (
    <Section title="Compliance audit trail" description="Blocked dials, DNC changes, consent and settings changes.">
      <QueryView query={trail}>
        {(rows) => (
          <Card>
            <CardContent className="divide-y divide-border py-2 text-[14px]">
              {rows.length === 0 ? <p className="py-4 text-muted-foreground">Nothing yet.</p> : null}
              {rows.map((r) => (
                <div key={r.id} className="flex flex-wrap justify-between gap-2 py-3">
                  <span className="font-mono text-[13px]">{r.action}</span>
                  <span className="text-muted-foreground">{Array.isArray((r.metadata as { blocks?: string[] }).blocks) ? (r.metadata as { blocks: string[] }).blocks.join(', ') : ''}</span>
                  <span className="text-xs text-muted-foreground">{formatDateTime(r.createdAt)}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </QueryView>
    </Section>
  );
}
